import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
} from "@bleu.builders/ui";
import React from "react";

import { formatDate } from "#/lib/formatDate";

const STATUS_MAPPING = {
  incomplete: "Incompleta",
  incomplete_expired: "Incompleta expirada",
  trialing: "Em teste",
  active: "Ativa",
  past_due: "Atrasada",
  canceled: "Cancelada",
  unpaid: "Não paga",
  paused: "Pausada",
};

const CURRENT_PLAN = ({ organization }) => {
  const info = [
    {
      name: "Plano",
      element: organization.plan?.name,
    },
    {
      name: "Status",
      element: (
        <Badge>{STATUS_MAPPING[organization.subscription.status]}</Badge>
      ),
    },
    {
      name: "Ciclo atual começa em",
      element: organization.subscription.current_period_start
        ? formatDate(organization.subscription.current_period_start)
        : "",
    },
    {
      name: "Ciclo atual termina em",
      element: organization.subscription.current_period_end
        ? formatDate(organization.subscription.current_period_end)
        : "",
    },
  ];

  if (organization.subscription.status === "trialing") {
    info.push({
      name: "Trial termina em",
      element: organization.subscription.trial_ends_at
        ? formatDate(organization.subscription.trial_ends_at)
        : "",
    });
  }

  return info;
};

const CURRENT_EXTRA_SEATS_PLAN = ({ organization }) => {
  return [
    {
      name: "Plano",
      element: organization.extra_seats_plan?.name,
    },
    {
      name: "Status",
      element: (
        <Badge>
          {STATUS_MAPPING[organization.extra_seats_subscription.status]}
        </Badge>
      ),
    },
    {
      name: "Ciclo começa em",
      element: organization.extra_seats_subscription.current_period_start
        ? formatDate(organization.extra_seats_subscription.current_period_start)
        : "",
    },
    {
      name: "Ciclo atual termina em",
      element: organization.extra_seats_subscription.current_period_end
        ? formatDate(organization.extra_seats_subscription.current_period_end)
        : "",
    },
  ];
};

const CURRENT_REUSABLE_DIAGNOSIS_LINK_CREATION_PLAN = ({ organization }) => {
  return [
    {
      name: "Plano",
      element: organization.reusable_diagnosis_link_creation_plan?.name,
    },
    {
      name: "Status",
      element: (
        <Badge>
          {
            STATUS_MAPPING[
              organization.reusable_diagnosis_link_creation_subscription.status
            ]
          }
        </Badge>
      ),
    },
    {
      name: "Ciclo começa em",
      element: organization.reusable_diagnosis_link_creation_subscription
        .current_period_start
        ? formatDate(
            organization.reusable_diagnosis_link_creation_subscription
              .current_period_start,
          )
        : "",
    },
    {
      name: "Ciclo atual termina em",
      element: organization.reusable_diagnosis_link_creation_subscription
        .current_period_end
        ? formatDate(
            organization.reusable_diagnosis_link_creation_subscription
              .current_period_end,
          )
        : "",
    },
  ];
};

function CurrentPlanCard({ organization }) {
  return (
    <Card className="max-w-none mr-10 rounded-lg bg-card text-card-foreground shadow-sm border-2">
      <CardHeader>
        {organization.subscription?.ends_at && (
          <div
            className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 mb-4 rounded relative flex items-center justify-center gap-1"
            role="alert"
          >
            <strong className="font-bold">Aviso:</strong>
            <span className="block sm:inline">
              Essa assinatura se encerra dia{" "}
              {formatDate(organization.subscription.ends_at)}
            </span>
          </div>
        )}
        <CardTitle className="text-xl font-semibold leading-none tracking-tight pl-0">
          Assinatura
        </CardTitle>
        <CardDescription className="text-sm text-muted-foreground pl-0">
          Aqui está um resumo da assinatura atual da organização.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 mx-4">
        {organization.subscription ? (
          <div className="grid grid-cols-2 gap-2">
            {CURRENT_PLAN({ organization }).map((item, index) => {
              return (
                item.element && (
                  <div key={index} className="space-y-1.5">
                    <Label htmlFor={item.name} className="font-bold">
                      {item.name}
                    </Label>
                    <div>{item.element}</div>
                  </div>
                )
              );
            })}
          </div>
        ) : (
          <div
            className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 mb-4 rounded relative flex items-center justify-center gap-1"
            role="alert"
          >
            <span className="block sm:inline">Cliente sem assinatura.</span>
          </div>
        )}
      </CardContent>
      {organization.extra_seats_subscription && (
        <>
          <CardHeader className="pt-0">
            <CardTitle className="text-xl font-semibold leading-none tracking-tight pl-0">
              Assinatura de Seats Extras
            </CardTitle>
            <CardDescription className="text-sm text-muted-foreground pl-0">
              Resumo da assinatura de Seats Extras da organização.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 mx-4">
            <div className="grid grid-cols-2 gap-2">
              {CURRENT_EXTRA_SEATS_PLAN({ organization }).map((item, index) => {
                return (
                  item.element && (
                    <div key={index} className="space-y-1.5">
                      <Label htmlFor={item.name} className="font-bold">
                        {item.name}
                      </Label>
                      <div>{item.element}</div>
                    </div>
                  )
                );
              })}
            </div>
          </CardContent>
        </>
      )}
      {organization.reusable_diagnosis_link_creation_subscription && (
        <>
          <CardHeader className="pt-0">
            <CardTitle className="text-xl font-semibold leading-none tracking-tight pl-0">
              Assinatura de Links de Diagnóstico Reutilizáveis
            </CardTitle>
            <CardDescription className="text-sm text-muted-foreground pl-0">
              Resumo da assinatura de Links de Diagnóstico Reutilizáveis da
              organização.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 mx-4">
            <div className="grid grid-cols-2 gap-2">
              {CURRENT_REUSABLE_DIAGNOSIS_LINK_CREATION_PLAN({
                organization,
              }).map((item, index) => {
                return (
                  item.element && (
                    <div key={index} className="space-y-1.5">
                      <Label htmlFor={item.name} className="font-bold">
                        {item.name}
                      </Label>
                      <div>{item.element}</div>
                    </div>
                  )
                );
              })}
            </div>
          </CardContent>
        </>
      )}
    </Card>
  );
}

export function Subscription({ organization }) {
  return (
    <div className="size-full flex gap-10 flex-col">
      <CurrentPlanCard organization={organization} />
    </div>
  );
}
